import moment, { Moment } from 'moment';
import { useEffect, useRef, useState } from 'react';
import { hapticsImpactLight } from '../../../utils/haptics';
import { Calendar, ChevronDown } from 'react-feather';
import Menu from '@mui/material/Menu';

interface CustomExamDatePickerProps {
  value: Moment | null;
  onChange: (date: Moment | null) => void;
  disabled?: boolean;
  inline?: boolean;
  skipDateSelection?: () => void;
}

const CustomExamDatePicker: React.FC<CustomExamDatePickerProps> = ({
  value,
  onChange,
  disabled = false,
  inline = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const [view, setView] = useState<'year' | 'month' | 'day'>('month');
  const [selectedYear, setSelectedYear] = useState<number>(moment().year());
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  const menuRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (value) {
      setSelectedYear(value.year());
      setSelectedMonth(value.month());
      setSelectedDay(value.date());
      setView('day');
    } else {
      setSelectedYear(moment().year());
      setSelectedMonth(null);
      setSelectedDay(null);
      setView('month');
    }
  }, [value]);

  const handleYearSelect = (year: number) => {
    if (disabled) return;
    setSelectedYear(year);
    setView('month');
    if (selectedMonth !== null && selectedDay !== null) {
      let potentialDate = moment({
        year,
        month: selectedMonth,
        day: selectedDay,
      });

      if (!potentialDate.isValid()) {
        potentialDate = moment({
          year,
          month: selectedMonth,
          day: moment({ year, month: selectedMonth }).daysInMonth(),
        });
      }
      onChange(potentialDate);
    }
    hapticsImpactLight();
  };

  const handleMonthSelect = (month: number) => {
    if (disabled) return;
    setSelectedMonth(month);
    setView('day');
    if (selectedDay !== null) {
      let potentialDate = moment({
        year: selectedYear,
        month,
        day: selectedDay,
      });

      if (!potentialDate.isValid()) {
        potentialDate = moment({
          year: selectedYear,
          month,
          day: moment({ year: selectedYear, month }).daysInMonth(),
        });
      }
      onChange(potentialDate);
    }
    hapticsImpactLight();
  };

  const handleDaySelect = (day: number) => {
    if (disabled) return;
    setSelectedDay(day);
    if (selectedMonth !== null) {
      const newDate = moment({ year: selectedYear, month: selectedMonth, day });
      if (newDate.isValid()) {
        onChange(newDate);
      } else {
        console.error(
          '#### handleDaySelect created an invalid date unexpectedly:',
          { year: selectedYear, month: selectedMonth, day }
        );
      }
    }
    hapticsImpactLight();
  };

  const isDateDisabled = (date: Moment) => {
    const fourYearsInThePast = moment().subtract(4, 'years').startOf('day');
    const fourYearsFromNow = moment().add(4, 'years').startOf('day');
    return (
      disabled ||
      date.isBefore(fourYearsInThePast) ||
      date.isAfter(fourYearsFromNow)
    );
  };

  const isMonthDisabled = (year: number, month: number) => {
    if (disabled) return true;
    const daysInMonth = moment({ year, month }).daysInMonth();
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = moment({ year, month, day }).startOf('day');
      if (!isDateDisabled(currentDate)) {
        return false;
      }
    }
    return true;
  };

  const renderDaysForMonth = () => {
    if (selectedMonth === null) return [];
    const daysInMonth = moment({
      year: selectedYear,
      month: selectedMonth,
    }).daysInMonth();

    // Get the first day of the month (0-6, where 0 is Sunday)
    const firstDayOfMonth = moment({
      year: selectedYear,
      month: selectedMonth,
      day: 1,
    }).day();

    // Create array of day labels
    const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Create array of empty cells for days before the first of the month
    const emptyCells = Array.from({ length: firstDayOfMonth }, (_, i) => (
      <div key={`empty-${i}`} className="days_day empty"></div>
    ));

    // Create array of day cells
    const dayCells = Array.from({ length: daysInMonth }, (_, i) => {
      const currentDate = moment
        .utc({ year: selectedYear, month: selectedMonth, day: i + 1 })
        .startOf('day');
      const isDisabled = isDateDisabled(currentDate);

      return (
        <button
          key={i}
          className={`days_day ${selectedDay === i + 1 ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
          onClick={() => {
            if (!isDisabled && !disabled) {
              handleDaySelect(i + 1);
            }
          }}
          disabled={isDisabled || disabled}
        >
          <span>{i + 1}</span>
        </button>
      );
    });

    return (
      <>
        <div className="days_labels">
          {dayLabels.map((label, _) => (
            <div key={label} className="days_label">
              {label}
            </div>
          ))}
        </div>
        <div className="days_grid">
          {emptyCells}
          {dayCells}
        </div>
      </>
    );
  };

  const renderCalendar = () => {
    return (
      <>
        <div className="custom-exam-date-picker_views">
          {view === 'year' && (
            <div className="custom-exam-date-picker_views_view">
              <div className="years">
                {Array.from({ length: 8 }, (_, i) => (
                  <div key={i}>
                    <button
                      className={`years_year ${selectedYear === moment().year() + i ? 'active' : ''}`}
                      onClick={() => handleYearSelect(moment().year() + i)}
                      disabled={disabled}
                    >
                      {moment().year() + i}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {view === 'month' && (
            <div className="custom-exam-date-picker_views_view">
              <div className="custom-exam-date-picker_views_view_header">
                <button onClick={() => setView('year')} disabled={disabled}>
                  {selectedYear}
                  <ChevronDown />
                </button>
              </div>
              <div className="months">
                {Array.from({ length: 12 }, (_, i) => {
                  const isMonthButtonDisabled = isMonthDisabled(
                    selectedYear,
                    i
                  );
                  return (
                    <div key={i}>
                      <button
                        className={`months_row_month ${
                          selectedMonth === i ? 'active' : ''
                        } ${isMonthButtonDisabled ? 'disabled' : ''}`}
                        onClick={() => {
                          if (!isMonthButtonDisabled && !disabled) {
                            handleMonthSelect(i);
                          }
                        }}
                        disabled={isMonthButtonDisabled || disabled}
                      >
                        {moment().month(i).format('MMMM')}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {view === 'day' && (
            <div
              className="custom-exam-date-picker_views_view"
              style={{ maxWidth: '20rem', margin: '0 auto' }}
            >
              <div className="custom-exam-date-picker_views_view_header">
                <button onClick={() => setView('month')} disabled={disabled}>
                  {selectedMonth !== null
                    ? moment().month(selectedMonth).format('MMMM')
                    : 'Select Month'}
                  <ChevronDown />
                </button>
                <button onClick={() => setView('year')} disabled={disabled}>
                  {selectedYear}
                  <ChevronDown />
                </button>
              </div>
              <div className="days">{renderDaysForMonth()}</div>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="custom-exam-date-picker">
      {!inline && (
        <button
          ref={menuRef}
          id="calendar-button"
          className="button button--open-picker"
          onClick={() => setIsOpen(!isOpen)}
          aria-controls={isOpen ? 'calendar-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={isOpen ? 'true' : undefined}
          tabIndex={0}
        >
          {value ? moment(value).format('MMMM D, YYYY') : 'Select Date'}
          <Calendar />
        </button>
      )}
      {inline ? (
        renderCalendar()
      ) : (
        <Menu
          id="calendar-menu"
          anchorEl={menuRef.current}
          open={isOpen}
          onClose={() => setIsOpen(false)}
          MenuListProps={{
            'aria-labelledby': 'calendar-button',
          }}
        >
          {renderCalendar()}
        </Menu>
      )}
    </div>
  );
};

export default CustomExamDatePicker;
